/**
 * Company Repository Implementation
 * Implements the CompanyRepository interface
 * Coordinates between API calls and mappers
 */

import { CompanyRepository } from "../../domain/repositories/CompanyRepository";
import { CompanyEntity } from "../../domain/entities/CompanyEntity";
import {
  createCompany,
  listCompanies,
  updateCompany as updateCompanyApi,
  deleteCompany as deleteCompanyApi,
} from "../api/companyApi";
import { CompanyMapper } from "../mappers/CompanyMapper";
import { CreateCompanyRequestDTO } from "../models/CompanyDTOs";

export class CompanyRepositoryImpl implements CompanyRepository {
  async getCompanies(
    page: number = 1,
    limit: number = 50,
    search?: string
  ): Promise<{
    companies: CompanyEntity[];
    pagination: {
      current_page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  }> {
    const response = await listCompanies(page, limit, search);
    return CompanyMapper.toListCompaniesResult(response);
  }

  async getCompanyById(companyId: number): Promise<CompanyEntity> {
    const response = await listCompanies(1, 1000);
    const company = response.data.companies.find((c) => c.id === companyId);
    if (!company) {
      throw new Error("Company not found");
    }
    return CompanyMapper.toCompanyEntity(company);
  }

  async createCompany(data: CreateCompanyRequestDTO): Promise<{
    company: CompanyEntity;
    admin_credentials: { email: string; password: string };
  }> {
    const response = await createCompany(data);
    return CompanyMapper.toCreateCompanyResult(response);
  }

  async updateCompany(
    companyId: number,
    data: Partial<CompanyEntity>
  ): Promise<CompanyEntity> {
    // Convert entity to DTO format (boolean to number)
    const dtoData: any = { ...data };
    if (dtoData.msme !== undefined) {
      dtoData.msme = dtoData.msme ? 1 : 0;
    }
    if (dtoData.company_gst_enabled !== undefined) {
      dtoData.company_gst_enabled = dtoData.company_gst_enabled ? 1 : 0;
    }
    if (dtoData.is_active !== undefined) {
      dtoData.is_active = dtoData.is_active ? 1 : 0;
    }
    const response = await updateCompanyApi(companyId, dtoData);
    return CompanyMapper.toCompanyEntity(response.data.company);
  }

  async deleteCompany(companyId: number): Promise<void> {
    await deleteCompanyApi(companyId);
  }
}

